import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Brain, Trash2, Pencil, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Memories() {
  const [data, setData] = useState({ memories: [], limit: 20, count: 0 });
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/memories");
    setData(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { await api.delete(`/memories/${id}`); toast.success("Memory deleted"); load(); };
  const delAll = async () => { await api.delete("/memories"); setConfirmAll(false); toast.success("All memories deleted"); load(); };
  const saveEdit = async (id) => { await api.patch(`/memories/${id}`, { content: editVal }); setEditing(null); toast.success("Memory updated"); load(); };
  const toggleShare = async (m) => { await api.patch(`/memories/${m.id}`, { shared: !m.shared }); load(); };

  const exportMem = async () => {
    const res = await api.get("/memories/export");
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "talkmos-memories.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Memories exported");
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8" data-testid="memory-manager-panel">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Memories</div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#F4F1EA] md:text-4xl">What they remember</h1>
          <p className="mt-2 text-sm text-[#8C8578]">{data.count} of {data.limit} memories saved. Each companion only sees their own unless you share.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-memories" onClick={exportMem} variant="outline" size="sm" className="rounded-full border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">
            <Download className="mr-1 h-4 w-4" /> Export
          </Button>
          {data.count > 0 && (
            <Button data-testid="delete-all-memories" onClick={() => setConfirmAll(true)} variant="outline" size="sm" className="rounded-full border-[#3D3428] bg-transparent text-[#c98a8a] hover:bg-[#1A1714]">
              <Trash2 className="mr-1 h-4 w-4" /> Delete all
            </Button>
          )}
        </div>
      </div>

      {data.count === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" data-testid="memories-empty">
          <Brain className="mb-4 h-10 w-10 text-[#3D3428]" />
          <p className="font-display text-2xl text-[#F4F1EA]">No memories yet</p>
          <p className="mt-2 max-w-xs text-sm text-[#8C8578]">When you share something meaningful in a chat, your companion will ask if they can remember it.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.memories.map((m) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-[#2B241B] bg-[#12100E] p-4" data-testid={`memory-${m.id}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full bg-[#1A1714] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#D4AF37]">{m.category}</span>
                <span className="text-xs text-[#8C8578]">{m.character_name}</span>
                {m.shared && <span className="text-[10px] text-[#F3E5AB]">· shared</span>}
              </div>
              {editing === m.id ? (
                <div className="space-y-2">
                  <Textarea value={editVal} onChange={(e) => setEditVal(e.target.value)} className="border-[#3D3428] bg-[#0A0908] text-[#F4F1EA]" data-testid={`edit-memory-input-${m.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(m.id)} className="rounded-full bg-[#D4AF37] text-[#0A0908] hover:bg-[#E5C158]" data-testid={`save-memory-${m.id}`}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="rounded-full border-[#3D3428] bg-transparent text-[#C8C2B5]">Cancel</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#E9E4D8]">{m.content}</p>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-[#2B241B] pt-3">
                <label className="flex items-center gap-2 text-xs text-[#8C8578]">
                  <Share2 className="h-3.5 w-3.5" /> Share with other companions
                  <Switch data-testid={`share-memory-${m.id}`} checked={m.shared} onCheckedChange={() => toggleShare(m)} />
                </label>
                <div className="flex gap-1">
                  <button data-testid={`edit-memory-${m.id}`} onClick={() => { setEditing(m.id); setEditVal(m.content); }} className="rounded-lg p-2 text-[#8C8578] hover:bg-[#1A1714]"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-memory-${m.id}`} onClick={() => del(m.id)} className="rounded-lg p-2 text-[#c98a8a] hover:bg-[#1A1714]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete all memories?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#C8C2B5]">Every memory across all companions will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-all" onClick={delAll} className="bg-[#8B2626] text-[#F4F1EA] hover:bg-[#a32d2d]">Delete all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
